import { Button, confirm, toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { HelpCircle, Plus } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { MonthlyReportListItem } from "@/modules/management/monthly-report/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { monthlyReportApi } from "../api/client";
import { useMonthlyReportsQuery } from "../api/queries";
import {
  MonthlyReportTour,
  monthlyReportListTourSteps,
  useMonthlyReportTour,
} from "../tour";
import {
  MonthlyReportFilter,
  type MonthlyReportSearchParams,
} from "../ui/MonthlyReportFilter";
import { MonthlyReportTable } from "../ui/MonthlyReportTable";

const PAGE_SIZE = 20;

export function MonthlyReportListPage() {
  const nav = useNavigate({ from: "/app/management/monthly-reports" });
  const search = useSearch({
    from: "/app/management/monthly-reports",
  }) as MonthlyReportSearchParams & {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  };

  const offset = search.offset ?? 0;
  const limit = search.limit ?? PAGE_SIZE;

  const query = useMemo(
    () => ({
      offset,
      limit,
      sort: search.sort,
      periodYear: search.periodYear,
      periodMonth: search.periodMonth,
      departmentId: search.departmentId,
      search: search.search,
    }),
    [
      offset,
      limit,
      search.sort,
      search.periodYear,
      search.periodMonth,
      search.departmentId,
      search.search,
    ],
  );

  const listQuery = useMonthlyReportsQuery(query);
  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;

  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const canCreate = permissions.has(Permissions.mgmt_monthly_report.create);
  const canRead =
    permissions.has(Permissions.mgmt_monthly_report.read) ||
    permissions.has(Permissions.mgmt_monthly_report.list);
  const canUpdate = permissions.has(Permissions.mgmt_monthly_report.update);
  const canDelete = permissions.has(Permissions.mgmt_monthly_report.delete);

  const toSearch = useCallback(
    (overrides: Partial<typeof search>) => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? PAGE_SIZE,
      sort: search.sort,
      periodYear: search.periodYear,
      periodMonth: search.periodMonth,
      departmentId: search.departmentId,
      search: search.search,
      ...overrides,
    }),
    [search],
  );

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({ search: toSearch({ offset: newOffset, limit: newLimit }) });
  };

  const handleView = (row: MonthlyReportListItem) => {
    nav({
      to: "/app/management/monthly-reports/$id",
      params: { id: row.id },
    });
  };

  const handleEdit = (row: MonthlyReportListItem) => {
    nav({
      to: "/app/management/monthly-reports/$id/edit",
      params: { id: row.id },
    });
  };

  const handleDelete = async (row: MonthlyReportListItem) => {
    const ok = await confirm({
      title: "ລຶບລາຍງານປະຈໍາເດືອນ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບລາຍງານນີ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await monthlyReportApi.delete(row.id);
      toast.success("ລຶບລາຍງານສຳເລັດ");
      queryClient.invalidateQueries({
        queryKey: ["management", "monthly-reports"],
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: toSearch({
          offset: 0,
          limit,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
        }),
      });
    },
    [nav, toSearch, limit],
  );

  const handleNew = () => {
    nav({ to: "/app/management/monthly-reports/new" });
  };

  const sortBy = search.sort?.[0]?.field ?? "periodYear";
  const sortOrder = (search.sort?.[0]?.dir ?? "desc") as "asc" | "desc";

  const tour = useMonthlyReportTour("list");

  return (
    <>
      <Header />
      <Main>
        <MonthlyReportTour
          steps={monthlyReportListTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between"
          data-tourid="monthly-report-list-toolbar"
        >
          <div className="flex items-center gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-2xl">ລາຍງານປະຈໍາເດືອນ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                ສ້າງ ແລະ ຈັດກຸ່ມລາຍງານປະຈໍາເດືອນຕາມເດືອນ/ພະແນກ
              </p>
            </div>
          </div>
          {canCreate && (
            <Button variant="default" onClick={handleNew} className="gap-2">
              <Plus className="h-4 w-4" />
              ເພີ່ມລາຍງານ
            </Button>
          )}
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="monthly-report-list-filter"
        >
          <MonthlyReportFilter
            searchParams={{ ...search, limit: search.limit ?? PAGE_SIZE }}
            fromRoute="/app/management/monthly-reports"
          />
          <div data-tourid="monthly-report-list-table">
            <MonthlyReportTable
              data={data}
              isLoading={listQuery.isLoading}
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              onPaginationChange={handlePaginationChange}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortingChange={handleSortingChange}
              onView={canRead ? handleView : undefined}
              onEdit={handleEdit}
              onDelete={canDelete ? handleDelete : undefined}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
